/* eslint-disable arrow-body-style */

import React, { useState } from 'react';
import classnames from 'classnames';
import { Form } from 'tea-component';
import EmailSender from '@gp/components/EmailSender';
import useGenerateReportFileSilent from './_hooks/useGenerateReportFileSilent';
import { sendEmail } from '@gp/services/dpsReport/common';
import { validateEmail } from '@gp/utils/validators';
import { EmailSendResultStatus } from '@gp/components/EmailSender/enums';

import type { IBaseProps } from '@home/types/common';
import type { IDPSReportBaseVO } from '@gp/types/dpsReport/common';
import type { IEmailVO, IEmailSendResult } from '@gp/components/EmailSender/types';
import type { ITagInputValue } from '@gp/components/TagInput/types';

interface IDPSReportEmailSenderProps extends IBaseProps {
  disabled?: boolean;
  node: React.RefObject<HTMLDivElement | null>;
  dateTitle?: string;
  subTitle?: string;
  report?: IDPSReportBaseVO | null;
}

const getRecipientsValues = (payload?: ITagInputValue | null): string[] => {
  const { tags, input } = payload || {};
  const result = [];

  if (Array.isArray(tags) && tags.length > 0) {
    for (const tag of tags) {
      const { valid, value } = tag || {};
      if (!valid || !value) continue;
      result.push(value);
    }
  }

  if (!!input) {
    input?.split(';').forEach((item) => {
      const itemTrim = item?.trim();
      if (validateEmail(itemTrim)) {
        result.push(itemTrim);
      }
    });
  }

  return result;
};

const DPSReportEmailSender: React.FC<IDPSReportEmailSenderProps> = (props) => {
  const { style, className, disabled = false, node, dateTitle, subTitle, report } = props;
  const fileName = subTitle || 'DPS Weekly Newsletter';

  const { imageFile, pdfFile, startGenerate } = useGenerateReportFileSilent({
    node,
    fileName,
    report,
  });

  const [isSending, setIsSending] = useState(false);

  const [isTestSending, setIsTestSending] = useState(false);

  const handleSendEmail = async (payload: IEmailVO): Promise<IEmailSendResult> => {
    const { start_date: startDate, end_date: endDate } = report || {};
    const recipients = getRecipientsValues(payload?.recipients);
    const sendEmailResult: IEmailSendResult = { status: '', title: payload?.title || '', emails: [] };
    try {
      await startGenerate();
      const res = await sendEmail({
        ...payload,
        recipients,
        start_date: startDate || '',
        end_date: endDate || '',
        image: imageFile.current as any,
        pdf: pdfFile.current as any,
      });
      const { code, msg, data } = res || {};
      const { list } = data || {};
      console.warn('send email exception: ', msg);
      if (code !== 200) throw new Error(msg);
      // 部分发送成功，部分发送失败
      if (Array.isArray(list) && list.length > 0) {
        sendEmailResult.status = EmailSendResultStatus.Partial;
        sendEmailResult.emails = list.map((item) => item?.email);
      } else {
        // 全部发送成功
        sendEmailResult.status = EmailSendResultStatus.Success;
        sendEmailResult.emails = [];
      }
    } catch (exception) {
      // 全部发送失败
      sendEmailResult.status = EmailSendResultStatus.Failed;
      sendEmailResult.emails = recipients;
    }

    return sendEmailResult;
  };

  const handleSend = (payload: IEmailVO): Promise<IEmailSendResult> => {
    return new Promise((resolve) => {
      setIsSending(true);

      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      setTimeout(async () => {
        const sendEmailResult = await handleSendEmail(payload);
        setIsSending(false);
        resolve(sendEmailResult);
      }, 300);
    });
  };

  const handleTest = (payload: IEmailVO): Promise<IEmailSendResult> => {
    return new Promise((resolve) => {
      setIsTestSending(true);

      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      setTimeout(async () => {
        const sendEmailResult = await handleSendEmail(payload);
        setIsTestSending(false);
        resolve(sendEmailResult);
      }, 300);
    });
  };

  return (
    <EmailSender
      style={style}
      className={classnames('dbg-dps-report-email-sender', className)}
      sendLogKey="WORKSPACE_DPS_WEEKLY_REPORT_SEND_EMAIL_SUBMIT"
      disabled={disabled}
      isSending={isSending}
      isTestSending={isTestSending}
      onSend={handleSend}
      onTest={handleTest}
      onCancel={() => {}}
      onOpen={() => {}}
      header={
        <Form.Item label="DPS Newsletter">
          <Form.Text>DPS Newsletter {dateTitle}</Form.Text>
        </Form.Item>
      }
    />
  );
};

export default DPSReportEmailSender;
